import { createContext, useState, useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'wouter';
import { useAuth } from '@/hooks/useAuth';
import { getTourForRoute, getTourById } from '@/content/tours';
import type { PageTour } from '@/content/tours';

export interface TourContextType {
  /** Whether a tour is currently running */
  isRunning: boolean;
  /** The currently active tour definition */
  activeTour: PageTour | null;
  /** Current step index (0-based) */
  currentStep: number;
  /** Start a tour by ID or PageTour object */
  startTour: (tourOrId: string | PageTour) => void;
  /** Start the tour for the current route (if one exists) */
  startTourForCurrentRoute: () => void;
  /** End the current tour */
  endTour: () => void;
  /** Go to the next step */
  nextStep: () => void;
  /** Go to the previous step */
  prevStep: () => void;
  /** Whether a tour exists for the current route */
  hasTourForCurrentRoute: boolean;
}

export const TourContext = createContext<TourContextType | null>(null);

export function TourProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [location] = useLocation();
  const [isRunning, setIsRunning] = useState(false);
  const [activeTour, setActiveTour] = useState<PageTour | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const locationRef = useRef(location);

  // Track current location for tour cancellation on navigation
  useEffect(() => {
    // If the tour is running and the user navigated away, end it
    if (isRunning && locationRef.current !== location) {
      setIsRunning(false);
      setActiveTour(null);
      setCurrentStep(0);
    }
    locationRef.current = location;
  }, [location, isRunning]);

  const hasTourForCurrentRoute = getTourForRoute(location) !== null;

  const startTour = useCallback((tourOrId: string | PageTour) => {
    const tour = typeof tourOrId === 'string' ? getTourById(tourOrId) : tourOrId;
    if (tour && tour.steps.length > 0) {
      setActiveTour(tour);
      setCurrentStep(0);
      setIsRunning(true);
    }
  }, []);

  const startTourForCurrentRoute = useCallback(() => {
    const tour = getTourForRoute(locationRef.current);
    if (tour) {
      setActiveTour(tour);
      setCurrentStep(0);
      setIsRunning(true);
    }
  }, []);

  const endTour = useCallback(() => {
    // Write localStorage for dashboard backward compatibility
    if (activeTour?.id === 'dashboard' && user?.id) {
      localStorage.setItem(`onboarding_completed_${user.id}`, new Date().toISOString());
    }
    setIsRunning(false);
    setActiveTour(null);
    setCurrentStep(0);
  }, [activeTour, user?.id]);

  const nextStep = useCallback(() => {
    if (!activeTour) return;
    if (currentStep >= activeTour.steps.length - 1) {
      endTour();
    } else {
      setCurrentStep((s) => s + 1);
    }
  }, [activeTour, currentStep, endTour]);

  const prevStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep((s) => s - 1);
    }
  }, [currentStep]);

  // Dashboard auto-start logic: on "/" route, check localStorage, auto-start after 1s
  useEffect(() => {
    if (!user?.id) return;
    if (location !== '/') return;

    const completed = localStorage.getItem(`onboarding_completed_${user.id}`);
    if (!completed) {
      const timer = setTimeout(() => {
        const tour = getTourById('dashboard');
        if (tour) {
          setActiveTour(tour);
          setCurrentStep(0);
          setIsRunning(true);
        }
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [user?.id, location]);

  return (
    <TourContext.Provider
      value={{
        isRunning,
        activeTour,
        currentStep,
        startTour,
        startTourForCurrentRoute,
        endTour,
        nextStep,
        prevStep,
        hasTourForCurrentRoute,
      }}
    >
      {children}
    </TourContext.Provider>
  );
}
